import React, { useRef, useState, useEffect } from 'react';

// 1. SummaryTextBand 컴포넌트
const SummaryTextBand = ({ searchYear, setSearchYear, clientName, salesRank, profitRank, kpiData, onClientSearchClick }) => {
    const getRankColor = (rank) => (rank === 1 || rank === "1" ? '#2563eb' : '#10b981');

    const handleYearChange = (direction) => {
        const nextYear = Number(searchYear) + direction;
        setSearchYear(String(nextYear));
    };

    return (
        <div style={{ backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd', padding: '4px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '500', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', marginBottom: '10px', boxSizing: 'border-box', width: '100%' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <strong style={{ color: '#0c4a6e', fontSize: '14px' }}>{clientName || '고객사를 검색하세요'}</strong>
                        </div>
                        <div style={{ width: '1px', height: '14px', backgroundColor: '#bae6fd' }} />
                        <div>
                            <span style={{ color: '#0369a1', fontWeight: '600' }}>{searchYear}년 </span>
                            <span style={{ color: '#0369a1', fontWeight: '600' }}>매출액:</span>
                            <strong style={{ color: '#0c4a6e', marginLeft: '4px' }}>₩ {kpiData.sales.toLocaleString()}</strong>
                            <strong style={{ color: getRankColor(salesRank), marginLeft: '4px' }}>(매출 {salesRank}위)</strong>
                            <span style={{ color: '#0284c7', fontSize: '11px', fontWeight: '600', marginLeft: '4px' }}>[전사 비중 {kpiData.ratio}]</span>
                        </div>
                        <div style={{ width: '1px', height: '14px', backgroundColor: '#bae6fd' }} />
                        <div>
                            <span style={{ color: '#0369a1', fontWeight: '600' }}>매입액:</span>
                            <strong style={{ color: '#0c4a6e', marginLeft: '4px' }}>₩ {kpiData.buy.toLocaleString()}</strong>
                        </div>
                        <div style={{ width: '1px', height: '14px', backgroundColor: '#bae6fd' }} />
                        <div>
                            <span style={{ color: '#0369a1', fontWeight: '600' }}>이익액:</span>
                            <strong style={{ color: '#16a34a', marginLeft: '4px' }}>₩ {kpiData.profit.toLocaleString()}</strong>
                            <strong style={{ color: getRankColor(profitRank), marginLeft: '4px' }}>(이익 {profitRank}위)</strong>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', height: '28px', border: '1px solid #0284c7', borderRadius: '4px', backgroundColor: '#ffffff', overflow: 'hidden', boxSizing: 'border-box' }}>
                            <button onClick={() => handleYearChange(-1)} style={{ padding: '0 8px', height: '100%', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: '700', color: '#0369a1', cursor: 'pointer', outline: 'none' }}>◀</button>
                            <span style={{ padding: '0 6px', fontSize: '12px', fontWeight: '700', color: '#0369a1', userSelect: 'none', minWidth: '45px', textAlign: 'center' }}>{searchYear}년</span>
                            <button onClick={() => handleYearChange(1)} style={{ padding: '0 8px', height: '100%', border: 'none', background: 'transparent', fontSize: '11px', fontWeight: '700', color: '#0369a1', cursor: 'pointer', outline: 'none', borderLeft: '1px solid #e0f2fe' }}>▶</button>
                        </div>
                        <button onClick={onClientSearchClick} style={{ padding: '5px 12px', height: '28px', backgroundColor: '#ffffff', border: '1px solid #0284c7', borderRadius: '4px', fontSize: '12px', fontWeight: '600', color: '#0369a1', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', outline: 'none', boxSizing: 'border-box' }}>
                            <span>고객사 검색</span>
                        </button>
                    </div>
                </div>
            );
        };

// 2. CardItem 컴포넌트
const CardItem = ({ title, ratio, metrics, headerBg }) => {
    // 🎯 1. 데이터 추출 및 안전하게 숫자형 변환
    const sales = Math.max(0, Number(metrics?.sales ?? 0));
    const buy = Math.max(0, Number(metrics?.buy ?? 0));
    const profit = Math.max(0, Number(metrics?.profit ?? 0));

    // 🎯 2. 매출액 대비 퍼센트 비율 계산
    const buyRatio = sales > 0 ? (buy / sales) * 100 : 0;
    const profitRatio = sales > 0 ? (profit / sales) * 100 : 0;

    // 🎯 3. SVG 도넛 정밀 치수 계산 (반지름 16 기준, 원주율 = 100.53)
    const radius = 16;
    const circumference = 2 * Math.PI * radius;

    const buyDashoffset = circumference - (buyRatio / 100) * circumference;
    const profitDashoffset = circumference - (profitRatio / 100) * circumference;
    const profitRotation = -90 + (buyRatio / 100) * 360; 

    return (
        /* 원래 주신 외곽 프레임 구조 완벽 보존 (가로/세로 변형 방지) */
        <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', border: '1px solid #d1d5db', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxSizing: 'border-box', overflow: 'hidden', height: '100%' }}>
            
            {/* 상단 헤더 영역 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', backgroundColor: headerBg || '#f3f4f6', borderBottom: '1px solid #d1d5db' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                    <h3 style={{ margin: 0, fontWeight: '700', fontSize: '13px', color: '#1f2937', whiteSpace: 'normal', wordBreak: 'keep-all' }}>{title}</h3>
                    <span style={{ fontSize: '11px', color: '#4b5563', backgroundColor: 'rgba(0,0,0,0.06)', padding: '2px 6px', borderRadius: '4px', fontWeight: '600', marginLeft: '2px', flexShrink: 0 }}>
                        {metrics?.count ?? 0}건
                    </span>
                </div>
                {ratio && <span style={{ fontSize: '11px', color: '#4b5563', backgroundColor: 'rgba(255,255,255,0.7)', padding: '1px 6px', borderRadius: '3px', fontWeight: '500', flexShrink: 0, marginLeft: '4px' }}>{ratio}</span>}
            </div>
            
            {/* 본문 영역: 원래 레이어 상태 (gap: 60px, 패딩 0px, 마진 -20px) 완벽하게 유지 */}
            <div style={{ display: 'flex', padding: '6px 0px', alignItems: 'center', justifyContent: 'center', gap: '60px', flex: 1 }}>
                
                {/* 좌측: 큰 차트 크기(72px) 유지 */}
                <div style={{ position: 'relative', width: '72px', height: '72px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: '-24px' }}>
                    <svg width="100%" height="100%" viewBox="0 0 40 40">
                        {/* 연회색 베이스 */}
                        <circle cx="20" cy="20" r={radius} fill="transparent" stroke="#e2e8f0" strokeWidth="5.5" />
                        
                        {/* 매입액 레이어 */}
                        {buyRatio > 0 && (
                            <circle
                                cx="20"
                                cy="20"
                                r={radius}
                                fill="transparent"
                                stroke="#ea580c"
                                strokeWidth="5.5"
                                strokeDasharray={circumference}
                                strokeDashoffset={buyDashoffset}
                                strokeLinecap="butt" 
                                style={{ transform: 'rotate(-90deg)', transformOrigin: '20px 20px' }}
                            />
                        )}
                        
                        {/* 이익액 레이어 */}
                        {profitRatio > 0 && (
                            <circle
                                cx="20"
                                cy="20"
                                r={radius}
                                fill="transparent"
                                stroke="#16a34a"
                                strokeWidth="5.5"
                                strokeDasharray={circumference}
                                strokeDashoffset={profitDashoffset}
                                strokeLinecap="butt"
                                style={{ transform: `rotate(${profitRotation}deg)`, transformOrigin: '20px 20px' }}
                            />
                        )}
                    </svg>
                    {/* 차트 중앙 퍼센트 */}
                    <div style={{ position: 'absolute', fontSize: '11px', fontWeight: '800', color: '#1f2937' }}>
                        {metrics?.margin ?? 0}%
                    </div>
                </div>

                {/* 우측: 원래 위치 및 간격 유지하면서 금액 끝선만 맞춤 */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flexShrink: 0 }}>
                    
                    {/* 매출액 */}
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                        <span style={{ fontWeight: '600', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '5px', width: '56px', flexShrink: 0 }}>
                            <span style={{ width: '7px', height: '7px', backgroundColor: '#e2e8f0', borderRadius: '1.5px', flexShrink: 0 }} />
                            매출액
                        </span>
                        <span style={{ fontWeight: '600', color: '#1f2937', textAlign: 'right', width: '95px', whiteSpace: 'nowrap' }}>
                            ₩ {sales.toLocaleString()}
                        </span>
                    </div>

                    {/* 매입액 */}
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                        <span style={{ fontWeight: '600', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '5px', width: '56px', flexShrink: 0 }}>
                            <span style={{ width: '7px', height: '7px', backgroundColor: '#ea580c', borderRadius: '1.5px', flexShrink: 0 }} />
                            매입액
                        </span>
                        <span style={{ fontWeight: '600', color: '#ea580c', textAlign: 'right', width: '95px', whiteSpace: 'nowrap' }}>
                            ₩ {buy.toLocaleString()}
                        </span>
                    </div>

                    {/* 이익액 */}
                    <div style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                        <span style={{ fontWeight: '600', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '5px', width: '56px', flexShrink: 0 }}>
                            <span style={{ width: '7px', height: '7px', backgroundColor: '#16a34a', borderRadius: '1.5px', flexShrink: 0 }} />
                            이익액
                        </span>
                        <span style={{ fontWeight: '600', color: '#16a34a', textAlign: 'right', width: '95px', whiteSpace: 'nowrap' }}>
                            ₩ {profit.toLocaleString()}
                        </span>
                    </div>
                </div>

            </div>
        </div>
    );
};

// 3. 메인 대시보드 성과 래퍼 컴포넌트
const KPISummaryCards = ({ searchYear, setSearchYear, clientName, salesRank, profitRank, kpiCardsData, kpiData, onClientSearchClick }) => {
    const systemColors = { blue: '#e0f2fe', orange: '#ffedd5', green: '#dcfce7', gray: '#f3f4f6' };
    const scrollRef = useRef(null);
    const [showButtons, setShowButtons] = useState(false);
    
    // 🎯 처음과 끝 도달 상태를 관리하는 새로운 상태 값
    const [isAtStart, setIsAtStart] = useState(true);
    const [isAtEnd, setIsAtEnd] = useState(false);

    // 스크롤 위치를 계산하여 화살표 숨김 여부를 판별하는 함수
    const updateScrollStatus = () => {
        if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            
            // 1px 미만의 미세 오차 방지를 위해 Math.ceil 처리
            setIsAtStart(scrollLeft <= 4);
            setIsAtEnd(Math.ceil(scrollLeft + clientWidth) >= scrollWidth - 4);
        }
    };

    // 해상도 변경 및 컨텐츠 변경 시 초기 버튼 활성화 여부 체크
    useEffect(() => {
        const checkArrows = () => {
            if (scrollRef.current) {
                const { scrollWidth, clientWidth } = scrollRef.current;
                setShowButtons(scrollWidth > clientWidth);
                updateScrollStatus(); // 초기 위치 상태 동기화
            }
        };

        checkArrows();
        window.addEventListener('resize', checkArrows);
        return () => window.removeEventListener('resize', checkArrows);
    }, [kpiCardsData]);

    // 화살표 클릭 시 동작 함수
    const handleScroll = (direction) => {
        if (scrollRef.current) {
            const containerWidth = scrollRef.current.clientWidth;
            const scrollAmount = direction === 'left' ? -(containerWidth / 4) : (containerWidth / 4);
            scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    return (
        <div style={{ width: '100%', boxSizing: 'border-box' }}>
            <SummaryTextBand searchYear={searchYear} setSearchYear={setSearchYear} clientName={clientName} salesRank={salesRank} profitRank={profitRank} kpiData={kpiData} onClientSearchClick={onClientSearchClick} />
            
            <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center', boxSizing: 'border-box' }}>
                
                {/* 🎯 왼쪽 화살표: 버튼 노출 조건 충족 && 처음 위치가 아닐 때만 렌더링 */}
                {showButtons && !isAtStart && (
                    <button 
                        onClick={() => handleScroll('left')}
                        style={{ position: 'absolute', left: '-14px', zIndex: 10, width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #cbd5e1', backgroundColor: '#ffffff', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#475569', outline: 'none' }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                    >
                        ◀
                    </button>
                )}

                {/* 🎯 온스크롤(onScroll) 이벤트를 걸어 휠이나 화살표 이동 시 실시간으로 위치 체크 */}
                <div 
                    ref={scrollRef}
                    onScroll={updateScrollStatus}
                    style={{ display: 'flex', gap: '12px', width: '100%', overflowX: showButtons ? 'auto' : 'hidden', boxSizing: 'border-box', scrollSnapType: 'x mandatory', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    className="hide-kpi-scrollbar"
                >
                    <div className="kpi-card-wrapper" style={{ scrollSnapAlign: 'start' }}>
                        <CardItem title="총 누적 수주" ratio={`전사 대비 ${kpiCardsData.total.ratio}`} metrics={kpiCardsData.total} headerBg={systemColors.green} />
                    </div>
                    <div className="kpi-card-wrapper" style={{ scrollSnapAlign: 'start' }}>
                        <CardItem title={`${searchYear}년 수주`} ratio={`전사 대비 ${kpiCardsData.yearly.ratio}`} metrics={kpiCardsData.yearly} headerBg={systemColors.orange} />
                    </div>
                    <div className="kpi-card-wrapper" style={{ scrollSnapAlign: 'start' }}>
                        <CardItem title="프로젝트 (수주 - 기타매출 포함)" metrics={kpiCardsData.project} headerBg={systemColors.blue} />
                    </div>
                    <div className="kpi-card-wrapper" style={{ scrollSnapAlign: 'start' }}>
                        <CardItem title="유지보수 (수주)" metrics={kpiCardsData.maintenance} headerBg={systemColors.blue} />
                    </div>
                    <div className="kpi-card-wrapper" style={{ scrollSnapAlign: 'start' }}>
                        <CardItem title="프로젝트 (포캐스팅)" metrics={kpiCardsData.forecastProject} headerBg={systemColors.gray} />
                    </div>
                    <div className="kpi-card-wrapper" style={{ scrollSnapAlign: 'start' }}>
                        <CardItem title="유지보수 (포캐스팅)" metrics={kpiCardsData.forecastMaintenance} headerBg={systemColors.gray} />
                    </div>
                </div>

                {/* 🎯 오른쪽 화살표: 버튼 노출 조건 충족 && 마지막 위치가 아닐 때만 렌더링 */}
                {showButtons && !isAtEnd && (
                    <button 
                        onClick={() => handleScroll('right')}
                        style={{ position: 'absolute', right: '-14px', zIndex: 10, width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #cbd5e1', backgroundColor: '#ffffff', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#475569', outline: 'none' }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
                    >
                        ▶
                    </button>
                )}
            </div>

            <style>{`
                .hide-kpi-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .kpi-card-wrapper {
                    flex: 1 0 calc(25% - 9px);
                    min-width: 240px;
                    box-sizing: border-box;
                }
                @media (min-width: 2000px) {
                    .kpi-card-wrapper {
                        flex: 1 0 calc(16.6666% - 10px) !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default KPISummaryCards;
